import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { ProductDetailModal } from './ProductDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTOPLAY_MS = 5000;

const firstCategory = (raw) => {
  if (!raw) return '';
  return String(raw).split(',')[0].trim();
};

/**
 * Full-bleed autoplay hero slideshow. Pulls best-selling product images, layers a
 * brand-toned overlay with title, price and a "Shop now" CTA. Entire slide is
 * tappable — opens the product detail modal. "View All" jumps to that slide's
 * primary collection on the catalog page. Swipe-aware on touch.
 */
export const HeroSlideshow = ({ onAddToCart, onAddToWishlist }) => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | empty | error
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const touchMoved = useRef(false);

  const fetchSlides = React.useCallback(() => {
    let cancelled = false;
    setLoadState('loading');
    axios.get(`${API}/products/best-sellers?limit=5`, { timeout: 8000 })
      .then((r) => {
        if (cancelled) return;
        const list = (r.data || []).map((p) => ({
          ...p,
          slide_image: (p.images && p.images[0]) || p.image,
        })).filter((p) => p.slide_image);
        if (list.length === 0) {
          setSlides([]);
          setLoadState('empty');
          return;
        }
        setSlides(list);
        setIndex(0);
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setSlides([]);
        setLoadState('error');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = fetchSlides();
    return cleanup;
  }, [fetchSlides]);

  // Autoplay (only when we have multiple slides + not paused)
  useEffect(() => {
    if (paused || slides.length < 2) return undefined;
    const id = setInterval(() => setIndex(i => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, slides.length]);

  const goTo = (i) => setIndex(((i % slides.length) + slides.length) % slides.length);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.touches[0].clientX;
    touchMoved.current = false;
  };
  const onTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    if (touchStartX.current != null && Math.abs(touchEndX.current - touchStartX.current) > 10) {
      touchMoved.current = true;
    }
  };
  const onTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) {
      touchStartX.current = null; touchEndX.current = null; return;
    }
    const dx = touchStartX.current - touchEndX.current;
    if (Math.abs(dx) > 40) {
      if (dx > 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Loading skeleton
  if (loadState === 'loading') {
    return (
      <section
        className="relative h-[68vh] sm:h-[78vh] bg-gradient-to-br from-[#FAF9F7] via-[#F5F3F0] to-[#D4C4B0] animate-pulse"
        data-testid="hero-slideshow-loading"
      />
    );
  }

  // Graceful fallback when API failed or returned no slides — never leaves the
  // user stuck on an infinite skeleton.
  if (loadState !== 'ready' || slides.length === 0) {
    return (
      <section
        className="relative h-[68vh] sm:h-[78vh] bg-gradient-to-br from-[#FAF9F7] via-[#F5F3F0] to-[#D4C4B0] flex items-center"
        data-testid="hero-slideshow-fallback"
      >
        <div className="container mx-auto px-5 sm:px-10">
          <div className="max-w-xl text-[#2C2C2C]">
            <p className="font-montserrat text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#6B4E37] mb-3">
              Sachdeva Sons · Since 1985
            </p>
            <h1 className="font-seasons text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-3">
              Timeless Indian Ethnic Wear
            </h1>
            <p className="font-montserrat text-sm sm:text-base text-[#6B4E37] mb-5">
              {loadState === 'error'
                ? "We couldn't load today's highlights — but our full catalogue is just a tap away."
                : 'Discover our curated collection of cotton, muslin and party wear.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => navigate('/search?sort=newest')}
                data-testid="hero-fallback-shop"
                className="bg-[#6B4E37] hover:bg-[#5A4030] text-white font-montserrat tracking-[0.2em] uppercase text-xs px-6 py-5 sm:py-6"
              >
                Shop the Catalogue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              {loadState === 'error' && (
                <Button
                  variant="outline"
                  onClick={fetchSlides}
                  data-testid="hero-fallback-retry"
                  className="border-[#6B4E37] text-[#6B4E37] hover:bg-[#6B4E37] hover:text-white font-montserrat tracking-[0.2em] uppercase text-xs px-6 py-5 sm:py-6 bg-transparent"
                >
                  Retry
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const current = slides[index];
  const slideCategory = firstCategory(current.category);

  const openCurrent = () => {
    if (touchMoved.current) { touchMoved.current = false; return; }
    if (selected) return; // modal already open — clicks inside portaled dialog
                          // bubble through the React tree, ignore them here
    setSelected(current);
  };

  const viewCollection = (e) => {
    e?.stopPropagation();
    if (slideCategory) navigate(`/search?category=${encodeURIComponent(slideCategory)}`);
    else navigate('/search?sort=newest');
  };

  const shopNowClick = (e) => {
    e?.stopPropagation();
    setSelected(current);
  };

  return (
    <section
      className="relative h-[68vh] sm:h-[78vh] bg-[#2C2C2C] overflow-hidden select-none cursor-pointer"
      data-testid="hero-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={openCurrent}
      role="button"
      tabIndex={0}
      aria-label={`Open ${current.name}`}
      onKeyDown={(e) => { if (!selected && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelected(current); } }}
    >
      {/* Slides */}
      {slides.map((s, i) => (
        <div
          key={s.id + '-' + i}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          aria-hidden={i !== index}
        >
          <img
            src={s.slide_image}
            alt={s.name}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: '50% 30%' }}
          />
          {/* Brand gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C2C2C]/70 via-[#2C2C2C]/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/60 via-transparent to-transparent sm:hidden" />
        </div>
      ))}

      {/* Content overlay — text wrapper must NOT swallow click; clicks here also open modal */}
      <div className="absolute inset-0 flex items-end sm:items-center pointer-events-none">
        <div className="container mx-auto px-5 sm:px-10 pb-12 sm:pb-0">
          <div key={current.id} className="max-w-xl text-white animate-in fade-in slide-in-from-bottom-3 duration-700">
            <p className="font-montserrat text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#E8D8C5] mb-3">
              {(current.category || 'Featured').replace(/,(?=\S)/g, ', ')}
            </p>
            <h1 className="font-seasons text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-3">
              {current.name}
            </h1>
            <div className="flex items-baseline gap-3 mb-5">
              <span className="font-seasons text-2xl sm:text-3xl">₹{current.price?.toLocaleString('en-IN')}</span>
              {current.originalPrice && current.originalPrice > current.price && (
                <span className="font-montserrat text-sm text-[#E8D8C5] line-through">₹{current.originalPrice?.toLocaleString('en-IN')}</span>
              )}
              {current.discount && (
                <span className="bg-[#C4B5A0] text-[#2C2C2C] font-montserrat text-[10px] tracking-wider uppercase px-2 py-0.5 rounded">
                  {current.discount}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pointer-events-auto">
              <Button
                onClick={shopNowClick}
                data-testid="hero-shop-now"
                className="bg-white text-[#2C2C2C] hover:bg-[#FAF6EE] font-montserrat tracking-[0.2em] uppercase text-xs px-6 py-5 sm:py-6"
              >
                Shop Now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={viewCollection}
                data-testid="hero-view-all"
                className="border-white text-white hover:bg-white hover:text-[#2C2C2C] font-montserrat tracking-[0.2em] uppercase text-xs px-6 py-5 sm:py-6 bg-transparent"
              >
                View All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Arrows (desktop) */}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/15 hover:bg-white/30 backdrop-blur-sm text-white rounded-full p-3 transition"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next slide"
            data-testid="hero-next"
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/15 hover:bg-white/30 backdrop-blur-sm text-white rounded-full p-3 transition"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid="hero-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'}`}
            />
          ))}
        </div>
      )}

      {/* Product detail modal triggered by tap/Shop Now */}
      <ProductDetailModal
        product={selected}
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        onAddToCart={onAddToCart}
        onAddToWishlist={onAddToWishlist}
      />
    </section>
  );
};
